import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import { ITaskProgress } from './taskprogress.models';
import { getNextAction, getExecutionHealth } from './taskprogress.logic';
import ProgressApprovalPanel from './ProgressApprovalPanel';
import ProgressEvidencePanel from './ProgressEvidencePanel';
import TaskActionTimeline from './TaskActionTimeline';
import TaskProgressEditor from './TaskProgressEditor';
import TaskTimeline from './TaskTimeline';

type Props = {
  task?: ITaskProgress;
  tab?: number;
  onTabChange?: (tab: number) => void;
  onChanged?: () => void;
};

type State = {
};

export default class TaskInspector extends React.PureComponent<Props, State> {
//export default class TaskInspector extends React.PureComponent<Props, State> {
  state: State = {
  };

  componentDidUpdate(prevProps: Props) {
    // if (prevProps.task?.rid !== this.props.task?.rid) {
    //   this.setState({ tab: 0 });
    // }
  }

  fmtMoney = (v?: number) => {
    const n = Number(v || 0);

    if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
    if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;

    return n.toLocaleString();
  };

  renderOverview(task: ITaskProgress) {
    const physical = Number(task.apr || task.rpr || 0);
    const financial = Number(task.fpr || 0);
    const variance = financial - physical;

    // const health =
    //   Math.abs(variance) <= 10 ? '🟢 Aligned' :
    //   Math.abs(variance) >= 30 ? '🔴 High Variance' :
    //   variance > 10 ? '🟠 Financial Ahead' :
    //   '🟡 Progress Ahead';
    const nextAction = getNextAction(task);
    const health = getExecutionHealth(task);

    return (
      <Stack spacing={2}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {task.tkc}
          </Typography>

          <Typography variant="h6">
            {task.tkn}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {task.atn}
          </Typography>
        </Box>

        <Box>
            <Typography variant="caption" color="text.secondary">
              Next Action
            </Typography>

            <Button
              size="small"
              variant={nextAction.mode === 'primary' ? 'contained' : 'outlined'}
              color={nextAction.mode === 'warning' ? 'warning' : 'primary'}
              fullWidth
              sx={{ mt: 0.5 }}
              onClick={() => this.props.onTabChange?.(nextAction.tab)}
            >
              {nextAction.label}
            </Button>
          </Box>

        <Divider />

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip size="small" label={task.ssc || 'State'} />
          <Chip size="small" label={`Reported ${task.rpr || 0}%`} />
          <Chip size="small" label={`Approved ${task.apr || 0}%`} />
          {task.ccc && <Chip size="small" label={task.ccc} />}
        </Stack>

        <Box>
          <Typography variant="subtitle2">Execution Alignment</Typography>

          <Stack spacing={1} sx={{ mt: 1 }}>
            <Box>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption">Implementation</Typography>
                <Typography variant="caption">{physical.toFixed(1)}%</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, physical))} />
            </Box>

            <Box>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption">Financial Disbursement</Typography>
                <Typography variant="caption">{financial.toFixed(1)}%</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, financial))} />
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
            <Chip size="small" label={health.label} />
            <Chip size="small" label={`Variance ${variance.toFixed(1)}%`} />
            <Chip size="small" label={`Budget ${this.fmtMoney(task.aca)} / ${this.fmtMoney(task.pam)}`} />
          </Stack>
        </Box>

        <Box>
          <Typography variant="subtitle2">Summary</Typography>
          <Typography variant="body2" color="text.secondary">
            {task.sum || 'No progress summary captured yet.'}
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2">Constraints</Typography>
          <Typography variant="body2" color="text.secondary">
            {task.con || 'No constraints reported.'}
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2">Next Steps</Typography>
          <Typography variant="body2" color="text.secondary">
            {task.nxt || 'No next steps captured.'}
          </Typography>
        </Box>
      </Stack>
    );
  }

  render() {
    const { task, onChanged } = this.props;
    const tab = this.props.tab ?? 0;

    return (
      <Paper elevation={1} sx={{ p: 2, position: 'sticky', top: 16 }}>
        {!task ? (
          <Typography color="text.secondary">
            Select a task to inspect progress, evidence, workflow, and approvals.
          </Typography>
        ) : (
          <>
            <Tabs
              value={tab}
              onChange={(_, v) => this.props.onTabChange?.(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2 }}
            >
              <Tab label="Overview" />
              <Tab label="Progress" />
              <Tab label={`Evidence (${task.evc || 0})`} />
              <Tab label="Workflow" />
              <Tab label={`Approvals (${task.apc || 0})`} />
              <Tab label={"Timeline"} />
            </Tabs>
            

            {tab === 0 && this.renderOverview(task)}

            {/* {tab === 1 && (
              <Typography color="text.secondary">
                Progress editor will move here next.
              </Typography>
            )} */}

            {tab === 1 && (
              <TaskProgressEditor
                task={task}
                onChanged={onChanged}
              />
            )}

            {tab === 2 && (
              <ProgressEvidencePanel
                task={task}
                onChanged={onChanged}
              />
            )}

            {tab === 3 && (
              <TaskActionTimeline
                task={task}
                onChanged={onChanged}
              />
            )}

            {tab === 4 && (
              <ProgressApprovalPanel
                task={task}
                onChanged={onChanged}
              />
            )}

            {tab === 5 && (
              <TaskTimeline task={task} />
            )}
          </>
        )}
      </Paper>
    );
  }
}